import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { PiArrowCounterClockwise, PiMagnifyingGlass } from 'react-icons/pi';
import {
  EmployeeAssistanceStatus,
  EmployeeExecutionAdvisoryCategory,
  EmployeeExecutionBlockerCategory,
  type EmployeeAssistance,
} from 'src/api/generated';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { PageHeader } from 'src/modules/shared/components/molecules/page-header';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { ExecutionDateRangePicker } from 'src/modules/automations/components/molecules/employees/executions/execution-date-range-picker';
import { EmployeeAssistancesPanel } from 'src/modules/automations/components/organisms/employees/assistances/assistances-panel';
import {
  assistancesSearchSchema,
  type AssistancesSearch,
} from 'src/modules/automations/schemas/employees/assistances.schema';
import { executionSearchSchema } from 'src/modules/automations/schemas/employees/executions.schema';
import {
  normalizeExecutionDateToken,
  toExecutionDateQuery,
} from 'src/modules/automations/utils/executions/execution-date-filter.util';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';
import { Route as CommunicationsIndexRoute } from 'src/routes/communications/index';
import { Route as EmployeeExecutionRoute } from 'src/routes/employees/$employeeId/executions/$executionId';
import { Route as EmployeeAssistancesRoute } from 'src/routes/employees/assistances';
import { Route as EmployeesIndexRoute } from 'src/routes/employees/index';
import { employeesSearchSchema } from 'src/modules/automations/schemas/employees/employees.schema';
import {
  useEmployeeAssistances,
  useResolveEmployeeAssistance,
} from 'src/modules/shared/hooks/api/automations/employees/assistances';
import { useAllEmployees } from 'src/modules/shared/hooks/api/automations/employees/employees';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  AutomationEmployeeAssistancePermission,
  AutomationEmployeeExecutionPermission,
} from 'src/modules/shared/types/permissions.type';

const ALL_FILTER_VALUE = 'all';

/**
 * Renders the tenant-wide operational inbox for AI Agent work needing attention.
 *
 * @returns The assistance page with filtering, paging, and source navigation.
 */
export const EmployeeAssistancesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { hasPermission: canManageInternalAssistance } = useHasPermissions(
    AutomationEmployeeAssistancePermission.ManageInternal
  );
  const { hasPermission: canResolveAssistance } = useHasPermissions(
    AutomationEmployeeAssistancePermission.Resolve
  );
  const { hasPermission: canReadExecutionDetails } = useHasPermissions(
    AutomationEmployeeExecutionPermission.Read
  );
  const search = useSearch({
    from: EmployeeAssistancesRoute.id,
    select: (value: AssistancesSearch) => value,
  });
  const [conversationDraft, setConversationDraft] = useState(
    search.conversationId?.toString() ?? ''
  );
  const dateRange = useMemo(
    () => ({
      from: normalizeExecutionDateToken(search.from),
      to: normalizeExecutionDateToken(search.to),
    }),
    [search.from, search.to]
  );
  const dateQuery = useMemo(() => toExecutionDateQuery(dateRange), [dateRange]);
  const queryParams = useMemo(
    () => ({
      limit: 20,
      status: search.status,
      employeeId: search.employeeId,
      category: search.category,
      conversationId: search.conversationId,
      ...dateQuery,
    }),
    [dateQuery, search]
  );
  const {
    assistances,
    loading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    listIncomplete,
  } = useEmployeeAssistances(queryParams);
  const {
    resolveAssistance,
    resolvingAssistanceId,
    error: resolutionError,
  } = useResolveEmployeeAssistance();
  const {
    employees,
    loading: employeesLoading,
    error: employeesError,
    refetch: refetchEmployees,
    directoryIncomplete,
  } = useAllEmployees();
  const agentIdentities = useMemo(
    () =>
      new Map(
        employees.map((employee) => [
          employee.employeeId,
          {
            name:
              employee.personalityName ??
              t('automations.aiAgents.assistance.unknownAgent'),
            identifier:
              employee.name ?? t('automations.aiAgents.index.unnamed'),
          },
        ])
      ),
    [employees, t]
  );
  const parsedConversation = Number(conversationDraft);
  const conversationIsValid =
    conversationDraft.trim() === '' ||
    (Number.isInteger(parsedConversation) && parsedConversation > 0);
  const hasFilters =
    search.status !== EmployeeAssistanceStatus.OPEN ||
    [
      search.employeeId,
      search.category,
      search.conversationId,
      dateRange.from,
      dateRange.to,
    ].some((value) => value != null);
  const pageError = error ?? employeesError;

  useEffect(() => {
    setConversationDraft(search.conversationId?.toString() ?? '');
  }, [search.conversationId]);

  const updateSearch = (updates: Partial<AssistancesSearch>) => {
    void navigate({
      to: EmployeeAssistancesRoute.to,
      search: (previous) => ({
        ...assistancesSearchSchema.parse(previous),
        ...updates,
      }),
    });
  };

  const applyConversationFilter = () => {
    if (!conversationIsValid) return;
    updateSearch({
      conversationId:
        conversationDraft.trim() === '' ? undefined : parsedConversation,
    });
  };

  const resetFilters = () => {
    setConversationDraft('');
    void navigate({
      to: EmployeeAssistancesRoute.to,
      search: assistancesSearchSchema.parse({}),
    });
  };

  const handleBack = () => {
    void navigate({
      to: EmployeesIndexRoute.to,
      search: (previous) => employeesSearchSchema.parse(previous),
    });
  };

  const openExecution = (assistance: EmployeeAssistance) => {
    void navigate({
      to: EmployeeExecutionRoute.to,
      params: {
        employeeId: assistance.employeeId,
        executionId: assistance.latestExecutionId,
      },
      search: executionSearchSchema.parse({}),
    });
  };

  const openConversation = (assistance: EmployeeAssistance) => {
    if (!assistance.conversationId || !assistance.funnelId) return;
    void navigate({
      to: CommunicationsIndexRoute.to,
      search: communicationsSearchSchema.parse({
        phoneVersion: 'v1',
        conversationId: assistance.conversationId,
        funnelId: assistance.funnelId,
      }),
    });
  };

  const statusOptions = [
    {
      value: EmployeeAssistanceStatus.OPEN,
      content: t('automations.aiAgents.assistance.status.open'),
    },
    {
      value: EmployeeAssistanceStatus.RESOLVED,
      content: t('automations.aiAgents.assistance.status.resolved'),
    },
  ];
  const categoryOptions = [
    {
      value: ALL_FILTER_VALUE,
      content: t('automations.aiAgents.assistance.filters.allCategories'),
    },
    {
      value: EmployeeExecutionBlockerCategory.MISSING_CAPABILITY,
      content: t('automations.aiAgents.assistance.category.missingCapability'),
    },
    {
      value: EmployeeExecutionBlockerCategory.CONFIGURATION_UNAVAILABLE,
      content: t(
        'automations.aiAgents.assistance.category.configurationUnavailable'
      ),
    },
    {
      value: EmployeeExecutionBlockerCategory.TERMINAL_TOOL_FAILURE,
      content: t(
        'automations.aiAgents.assistance.category.terminalToolFailure'
      ),
    },
    {
      value: EmployeeExecutionBlockerCategory.REPEATED_TOOL_FAILURE,
      content: t(
        'automations.aiAgents.assistance.category.repeatedToolFailure'
      ),
    },
    {
      value: EmployeeExecutionBlockerCategory.TERMINAL_OPERATION_FAILURE,
      content: t(
        'automations.aiAgents.assistance.category.terminalOperationFailure'
      ),
    },
    ...(canManageInternalAssistance
      ? [
          {
            value: EmployeeExecutionAdvisoryCategory.MESSAGE_FIDELITY_REVIEW,
            content: t(
              'automations.aiAgents.assistance.category.messageFidelityReview'
            ),
          },
        ]
      : []),
  ];
  const employeeOptions = [
    {
      value: ALL_FILTER_VALUE,
      content: t('automations.aiAgents.assistance.filters.allAgents'),
    },
    ...employees.map((employee) => ({
      value: employee.employeeId,
      content: `${employee.personalityName ?? t('automations.aiAgents.assistance.unknownAgent')} · ${employee.name ?? t('automations.aiAgents.index.unnamed')}`,
    })),
  ];

  return (
    <div className="flex h-full flex-col">
      <Panel className="flex h-full min-h-0 flex-col">
        <PageHeader
          title={t('automations.aiAgents.assistance.title')}
          subtitle={t('automations.aiAgents.assistance.description')}
          onBack={handleBack}
          backLabel={t('automations.aiAgents.assistance.back')}
        />

        <PageFiltersBar
          contentClassName="sm:items-center"
          validationId="assistance-conversation-filter-error"
          validationMessage={
            conversationIsValid
              ? undefined
              : t('automations.aiAgents.assistance.filters.invalidConversation')
          }
        >
          <Select
            aria-label={t('automations.aiAgents.assistance.filters.status')}
            value={search.status}
            options={statusOptions}
            onChange={(event) => {
              const result = assistancesSearchSchema.shape.status.safeParse(
                event.target.value
              );
              if (result.success) updateSearch({ status: result.data });
            }}
            containerClassName="w-full sm:w-44"
          />
          <Select
            aria-label={t('automations.aiAgents.assistance.filters.category')}
            value={search.category ?? ALL_FILTER_VALUE}
            options={categoryOptions}
            onChange={(event) => {
              if (event.target.value === ALL_FILTER_VALUE) {
                updateSearch({ category: undefined });
                return;
              }
              const result = assistancesSearchSchema.shape.category.safeParse(
                event.target.value
              );
              if (result.success) updateSearch({ category: result.data });
            }}
            containerClassName="w-full sm:w-60"
          />
          <Select
            aria-label={t('automations.aiAgents.assistance.filters.agent')}
            value={search.employeeId ?? ALL_FILTER_VALUE}
            options={employeeOptions}
            loading={employeesLoading}
            onChange={(event) =>
              updateSearch({
                employeeId:
                  event.target.value === ALL_FILTER_VALUE
                    ? undefined
                    : event.target.value,
              })
            }
            containerClassName="w-full sm:w-56"
          />
          <form
            className="flex w-full gap-2 sm:w-auto"
            onSubmit={(event) => {
              event.preventDefault();
              applyConversationFilter();
            }}
          >
            <TextInput
              inputMode="numeric"
              aria-label={t(
                'automations.aiAgents.assistance.filters.conversation'
              )}
              aria-describedby={
                conversationIsValid
                  ? undefined
                  : 'assistance-conversation-filter-error'
              }
              placeholder={t(
                'automations.aiAgents.assistance.filters.conversationPlaceholder'
              )}
              value={conversationDraft}
              error={
                conversationIsValid
                  ? undefined
                  : t(
                      'automations.aiAgents.assistance.filters.invalidConversation'
                    )
              }
              onChange={(event) => setConversationDraft(event.target.value)}
              className="sm:w-44"
            />
            <Button
              type="submit"
              size="md"
              variant="secondary"
              appearance="outline"
              isIconOnly
              disabled={!conversationIsValid}
              aria-label={t(
                'automations.aiAgents.assistance.filters.applyConversation'
              )}
            >
              <PiMagnifyingGlass className="h-4 w-4" />
            </Button>
          </form>
          <ExecutionDateRangePicker
            value={dateRange}
            onApply={(range) =>
              updateSearch({ from: range.from, to: range.to })
            }
          />
          {hasFilters ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              appearance="ghost"
              onClick={resetFilters}
              className="w-full gap-2 sm:w-auto"
            >
              <PiArrowCounterClockwise className="h-4 w-4" />
              {t('automations.aiAgents.assistance.filters.reset')}
            </Button>
          ) : null}
        </PageFiltersBar>

        {pageError ? (
          <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-50 p-4">
            <ErrorState
              title={t('automations.aiAgents.assistance.errorTitle')}
              description={t(
                'automations.aiAgents.assistance.errorDescription'
              )}
              onAction={() => {
                if (error) void refetch();
                if (employeesError) void refetchEmployees();
              }}
              actionContent={t('automations.aiAgents.actions.retry')}
            />
          </div>
        ) : (
          <>
            {resolutionError ? (
              <Alert
                variant="danger"
                appearance="soft"
                message={t('automations.aiAgents.assistance.dismissError')}
                className="mx-4 mt-4 sm:mx-5"
              />
            ) : null}
            <EmployeeAssistancesPanel
              assistances={assistances}
              agentIdentities={agentIdentities}
              onOpenExecution={
                canReadExecutionDetails ? openExecution : undefined
              }
              onOpenConversation={openConversation}
              onDismissAssistance={
                canResolveAssistance
                  ? (assistance) => resolveAssistance(assistance.assistanceId)
                  : undefined
              }
              dismissingAssistanceId={resolvingAssistanceId}
              onLoadMore={() => void fetchNextPage()}
              hasMore={Boolean(hasNextPage)}
              isLoadingMore={isFetchingNextPage}
              isInitialLoading={loading && assistances.length === 0}
              directoryIncomplete={directoryIncomplete}
              assistanceListIncomplete={listIncomplete}
            />
          </>
        )}
      </Panel>
    </div>
  );
};
